import { FikoGrup } from '../FikoGrup';
import { FikoSahne } from '../FikoSahne';
import { ICamOperasyon, ITakim, TAKIM_AD } from './fiko-cam.types';
import { FikoCamDialog } from './fiko-cam-dialog';
import { FikoCamTakimDialog } from './fiko-cam-takim-dialog';

/**
 * FikoYT CAM — Aktivite ağaç node'u (CATIA: Manufacturing Program altındaki Machining Operation).
 * Ürün ağacında görünür; çift-tık → operasyon penceresi, sağ-tık → hesapla/simüle/bastır/sil.
 * Kalıcılık: op verisi Manufacturing Program userData['FikoCam']'da; node yüklemede placeholder, CAM açılınca yeniden kurulur.
 */
export class FikoCamAktivite extends FikoGrup {
  override type: any = 'FikoCamAktivite';
  op: ICamOperasyon;
  yonetici: any;

  constructor(sahne?: FikoSahne, op?: ICamOperasyon, yonetici?: any) {
    super(sahne, op?.ad || 'CAM Operasyonu');
    this.op = op as ICamOperasyon;
    this.yonetici = yonetici;
    this.userData['FikoArayuz'].agactaGorunur = true;
    this.userData['FikoArayuz'].listeGoster = false;
    this.userData['FikoArayuz'].simge = './simgeler/takimyolu.webp';
    this.etiketGuncelle();
    this.sagMenuKur();
  }

  private df(): any { return this.sahne?.anaGosterim?.DialogFiko; }

  /** Node etiketi: takım rozeti + associativity durumu (CATIA update): "⟳ Facing.1 · T1 Ø50" = model değişti, toolpath güncel değil. */
  etiketGuncelle(): void {
    const tk = this.yonetici?.takimlar?.find((t: any) => t.id === this.op?.takimId);
    this.name = (this.op?.bayat ? '⟳ ' : '') + (this.op?.ad || 'CAM') + (tk ? '  ·  T' + (tk.numara ?? '?') + ' Ø' + tk.cap : '');
  }

  /** CATIA Tool Change — operasyonun takımını değiştir (sağ menü alt seçenekleri). */
  private takimMenusu(): any[] {
    return (this.yonetici?.takimlar || []).map((tk: any) => ({
      baslik: 'T' + (tk.numara ?? '?') + '  ' + tk.ad, simge: 'wrench',
      tiklandi: () => { if (this.op) { this.op.takimId = tk.id; this.op.hesaplandi = false; this.yonetici?.hizHesapla?.(this.op); this.yonetici?.hesapla(this.op); this.etiketGuncelle(); this.yonetici?.durumKaydet?.(); this.sahne?.anaGosterim?.FikoYT?.degisti?.emit?.(); } },
    }));
  }

  dialogAc(): void {
    if (!this.op || !this.yonetici) return;
    this.df()?.ac({
      bilesen: FikoCamDialog,
      parametreler: { element: this.op, yonetici: this.yonetici },
      ayarlar: { baslik: this.op.ad, simgeResim: './simgeler/3d.webp' },
    });
  }

  tiklandi(): void { if (this.op && this.yonetici) { this.yonetici.makroGorselGoster?.(this.op); this.yonetici.secimGorselle?.(this.op); } }
  ciftTiklandi(): void { this.dialogAc(); }

  sagButonlar: any[] = [
    { isim: 'Düzenle', simge: 'edit', tiklandi: () => this.dialogAc() },
    { isim: 'Hesapla', simge: 'cogs', tiklandi: () => this.op && this.yonetici?.hesapla(this.op) },
    { isim: 'Simüle', simge: 'play', tiklandi: () => this.yonetici?._simAc?.(this) },   // CATIA: sadece bu operasyonun altındaki toolpath
  ];

  override sagMenu: any[] = [];
  /** Sağ menüyü kurar (CATIA: düzenle/hesapla/simüle/kopyala/bastır + Tool Change takım listesi + sil). Takım kütüphanesi değişince yönetici yeniden çağırır. */
  sagMenuKur(): void {
    this.sagMenu = [
      { baslik: 'Düzenle', simge: 'edit', tiklandi: () => this.dialogAc() },
      { baslik: 'Hesapla', simge: 'cogs', tiklandi: () => this.op && this.yonetici?.hesapla(this.op) },
      { baslik: 'Simüle Et', simge: 'play', tiklandi: () => this.yonetici?._simAc?.(this) },   // CATIA: sadece bu operasyonun altındaki toolpath
      { baslik: 'Makro Göster (3D)', simge: 'cube', tiklandi: () => this.yonetici?.makroGorselGoster?.(this.op) },
      { baslik: 'Kopyala / Dönüştür', simge: 'copy', tiklandi: () => this.yonetici?._copyAc?.(this.op) },
      { baslik: 'Bastır / Aç', simge: 'ban', tiklandi: () => { if (this.op) { this.op.bastirilmis = !this.op.bastirilmis; this.yonetici?.hesapla(); this.yonetici?.durumKaydet?.(); this.sahne?.anaGosterim?.FikoYT?.degisti?.emit?.(); } } },
      { tipi: 'Ayraç' },
      { baslik: 'Takım Değiştir (Tool Change):', simge: 'wrench' },
      ...this.takimMenusu(),
      { tipi: 'Ayraç' },
      { baslik: 'Sil', simge: 'trash alternate', renkSinif: 'red', tiklandi: () => this.op && this.yonetici?.operasyonSil(this.op) },
    ];
  }

  override toJSON(): any {
    // op verisi merkezi (Manufacturing Program userData) saklanır — node sadece iskelet
    return { type: this.type, isim: this.name, uuid: this.uuid, opId: this.op?.id };
  }

  static fromJson(sahne?: FikoSahne, parent?: any, json?: any): FikoCamAktivite {
    const node = new FikoCamAktivite(sahne, { ad: json?.isim, id: json?.opId } as any, null);
    if (json?.uuid) node.uuid = json.uuid;
    node.parent = parent as any;
    return node;
  }
}

/**
 * FikoYT CAM — Takım kaynağı ağaç node'u (CATIA Resource List / Tool Catalog).
 * Ürün ağacında "Takımlar" altında; çift-tık → grafiksel takım dialoğu, sağ-tık → sil.
 */
export class FikoCamTakimNode extends FikoGrup {
  override type: any = 'FikoCamTakimNode';
  takim: ITakim;
  yonetici: any;
  override sagMenu: any[] = [];

  constructor(sahne?: FikoSahne, takim?: ITakim, yonetici?: any) {
    super(sahne, 'Takım');
    this.takim = takim as ITakim;
    this.yonetici = yonetici;
    this.userData['FikoArayuz'].agactaGorunur = true;
    this.userData['FikoArayuz'].listeGoster = false;
    this.userData['FikoArayuz'].simge = './simgeler/takim.svg';
    this.etiketGuncelle();
    this.sagMenu = [
      { baslik: 'Düzenle', simge: 'edit', tiklandi: () => this.dialogAc() },
      { tipi: 'Ayraç' },
      { baslik: 'Sil', simge: 'trash alternate', renkSinif: 'red', tiklandi: () => this.takim && this.yonetici?.takimSil(this.takim) },
    ];
  }

  etiketGuncelle(): void {
    const t = this.takim;
    this.name = t ? ('T' + (t.numara ?? '?') + '  ' + t.ad + '  ·  ' + (TAKIM_AD[t.tip] || t.tip) + ' Ø' + t.cap) : 'Takım';
  }

  private df(): any { return this.sahne?.anaGosterim?.DialogFiko; }
  dialogAc(): void {
    if (!this.takim || !this.yonetici) return;
    this.df()?.ac({
      bilesen: FikoCamTakimDialog,
      parametreler: { element: this.takim, yonetici: this.yonetici },
      ayarlar: { baslik: 'Takım Tanımı', simgeResim: './simgeler/takim.svg' },
    });
  }
  ciftTiklandi(): void { this.dialogAc(); }

  override toJSON(): any { return { type: this.type, isim: this.name, uuid: this.uuid, takimId: this.takim?.id }; }
  static fromJson(sahne?: FikoSahne, parent?: any, json?: any): FikoCamTakimNode {
    const node = new FikoCamTakimNode(sahne, { id: json?.takimId, ad: json?.isim } as any, null);
    if (json?.uuid) node.uuid = json.uuid;
    node.parent = parent as any;
    return node;
  }
}
